import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay, RichTextEditor } from '@factorialco/factorial-one-react/dist/experimental'
import { resultType } from '@factorialco/factorial-one-react/dist/experimental'

import Rating from 'modules/core/components/Rating'
import TextArea from 'modules/core/components/TextArea'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { AnswerQuestion } from 'modules/questionnaires/components/Answer/AnswerQuestion'
import {
  RatingAnswer as AnswerType,
  RatingQuestion as QuestionType,
} from 'modules/questionnaires/lib/types'

type Props = {
  question: QuestionType
  answer?: AnswerType
  position?: number
  readonly?: boolean
  onChange: (answer: AnswerType) => void
  hasAttemptedSubmit?: boolean
}

export const AnswerRating: React.FC<Props> = ({
  question,
  answer,
  position,
  readonly,
  onChange,
  hasAttemptedSubmit = false,
}) => {
  const { editorConfig, canUseRichTextEditor, loading: loadingRichTextEditor } = useRichTextEditor()

  const [ratingAnswer, setRatingAnswer] = React.useState<AnswerType['answerRating']>({
    value: answer?.answerRating?.value ?? undefined,
    comment: answer?.answerRating?.comment ?? undefined,
  })

  const [hoveredRating, setHoveredRating] = React.useState<number | null>(null)
  const range = question.scale.map((r) => r.value)
  const min = Math.min(...range)
  const max = Math.max(...range)

  const ratingOption = question.scale.find((option) => option.value === ratingAnswer?.value)
  const ratingLabels = question.scale.reduce<Record<number, string>>((acc, item) => {
    const label = { [item.value]: `${item.value}. ${item.text}` }
    return { ...acc, ...label }
  }, {})

  const ratingLabel = ratingOption ? ratingLabels[ratingOption.value] : ''

  const handleRatingChange = (value: number) => {
    const currentValue = ratingAnswer?.value
    const updatedValue = value === currentValue ? null : value
    setRatingAnswer({ ...ratingAnswer, value: updatedValue })
    onChange({
      ...answer,
      uuid: question.uuid,
      answerRating: {
        value: updatedValue,
        comment: ratingAnswer?.comment,
      },
    })
  }

  const handleCommentChange = (comment: string) => {
    setRatingAnswer({ ...ratingAnswer, comment: comment })
    onChange({
      ...answer,
      uuid: question.uuid,
      answerRating: {
        value: ratingAnswer?.value,
        comment,
      },
    })
  }

  const handleCommentChangeRichText = (data: resultType) => {
    const newValue = data?.value ?? ''
    setRatingAnswer({ ...ratingAnswer, comment: newValue })
    onChange({
      ...answer,
      uuid: question.uuid,
      answerRating: {
        value: ratingAnswer?.value,
        comment: newValue,
      },
    })
  }

  const isMandatoryAndEmpty = question.mandatory && ratingAnswer?.value == null

  return (
    <AnswerQuestion
      uuid={question.uuid}
      position={position}
      title={question.title}
      description={question.description}
    >
      <Box gap="s16">
        <Box gap="s8">
          <Rating
            aria-labelledby={`question-${question.uuid}`}
            hasTooltip
            mandatory={isMandatoryAndEmpty}
            min={min}
            max={max}
            value={ratingAnswer?.value ?? null}
            onClick={handleRatingChange}
            readonly={readonly}
            tooltipLabel={hoveredRating !== null ? ratingLabels[hoveredRating] : ''}
            onHover={setHoveredRating}
            error={
              hasAttemptedSubmit && isMandatoryAndEmpty
                ? i18n.t('performance_process.reviews.answer_evaluation.missing_mandatory_field')
                : undefined
            }
          />
          <Text.Normal size="200" weight="medium" color="grey800">
            {ratingLabel}
          </Text.Normal>
        </Box>
        {question.withComment &&
          !loadingRichTextEditor &&
          (canUseRichTextEditor ? (
            readonly ? (
              ratingAnswer?.comment && (
                <Box
                  border={{
                    all: {
                      color: 'grey400',
                      width: 's1',
                      style: 'solid',
                    },
                  }}
                  borderRadius={{ all: 'abs016' }}
                  padding="s16"
                  background="grey000White"
                >
                  <RichTextDisplay content={ratingAnswer.comment} />
                </Box>
              )
            ) : (
              <RichTextEditor
                {...editorConfig}
                title={question.title}
                onChange={handleCommentChangeRichText}
                placeholder={i18n.t('questionnaires.answers.rating.comment_placeholder')}
                initialEditorState={{
                  content: ratingAnswer?.comment || '',
                }}
                maxCharacters={5000}
              />
            )
          ) : (
            <TextArea
              label={`${question.title} ${i18n.t(
                'questionnaires.answers.rating.comment_placeholder'
              )}`}
              hideLabel
              placeholder={i18n.t('questionnaires.answers.rating.comment_placeholder')}
              value={ratingAnswer?.comment || ''}
              onChange={handleCommentChange}
              readonly={readonly}
            />
          ))}
      </Box>
    </AnswerQuestion>
  )
}
